import React from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import TextButton from "@shared/v2/TextButton";
import ViewWebActivityModal from "./modals/ViewWebActivityModal";
import { valueOrZero, lastVisit, visits } from "../../shared/WebActivityHelpers";
import * as actions from "../actions/personDetailActionCreators";
import Loading from "../../Goals/components/Loader";
import CollapsibleElement from "./CollapsibleElement";

const hasNoActivity = (
  personAttributes = {
    idx_visits: null,
    idx_last_visit: null,
    idx_property_views: null,
    idx_favorites: null,
    idx_average_price: null,
  },
) =>
  personAttributes.idx_visits === null &&
  personAttributes.idx_last_visit === null &&
  personAttributes.idx_property_views === null &&
  personAttributes.idx_favorites === null &&
  personAttributes.idx_average_price === null;

const formatPrice = (price) => {
  if (price === null) return 0;

  return `$${price > 1000000 ? parseFloat(price / 1000000).toFixed(2) : parseInt(Math.floor(price / 1000), 10)}${price > 1000000 ? "M" : "K"}`;
};

const WebActivity = ({ openPlatformCTA }) => {
  const dispatch = useDispatch();
  const featureFlags = useSelector((state) => state.layout.featureFlags);
  const person = useSelector((state) => state.personDetail.person);
  const webActivityModalOpen = useSelector((state) => state.personDetail.webActivityModalOpen);

  const webActivityButtonText = featureFlags.platformAccount ? "VIEW" : "LEARN MORE";
  const webActivityButtonClick = featureFlags.platformAccount
    ? () => dispatch(actions.openWebActivityModal())
    : () => openPlatformCTA();

  const openWebActivityModal = () => dispatch(actions.openWebActivityModal());
  const closeWebActivityModal = () => dispatch(actions.closeWebActivityModal());

  const personAttributes = person.data?.attributes;

  return (
    <CollapsibleElement
      id="pdp-web-activity"
      title="Web Activity"
      buttonText={webActivityButtonText}
      buttonClickFunction={webActivityButtonClick}
    >
      {!person.data && <Loading />}
      {hasNoActivity(personAttributes) ? (
        <div className="tw-text-12d">
          See when a contact visits your Brivity website and which homes they view.
        </div>
      ) : (
        <div id="web-activity" className="tw-flex tw-flex-col tw-gap-[12px]">
          <div className="tw-flex tw-justify-between">
            <div>
              <span className="tw-text-11d tw-uppercase tw-font-bold">
                Visits: {visits(personAttributes.idx_visits)}
              </span>
            </div>
            <div>
              <span className="tw-text-11d tw-font-bold">
                <span className="tw-uppercase">Last Visit: </span>
                {lastVisit(personAttributes.idx_last_visit)}
              </span>
            </div>
          </div>
          <div className="tw-shadow-card tw-rounded-[8px] tw-p-[10px] tw-flex tw-justify-between tw-gap-[8px]">
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-[4px]">
              <span className="tw-text-11d tw-uppercase tw-font-bold">Views</span>
              <TextButton size="medium" className="tw-text-18d" onClick={openWebActivityModal}>
                {valueOrZero(personAttributes.idx_property_views)}
              </TextButton>
            </div>
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-[4px]">
              <span className="tw-text-11d tw-uppercase tw-font-bold">Favorites</span>
              <TextButton size="medium" className="tw-text-18d" onClick={openWebActivityModal}>
                {valueOrZero(personAttributes.idx_favorites)}
              </TextButton>
            </div>
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-[4px]">
              <span className="tw-text-11d tw-uppercase tw-font-bold">Avg. Price</span>
              <TextButton size="medium" className="tw-text-18d" onClick={openWebActivityModal}>
                {formatPrice(personAttributes.idx_average_price)}
              </TextButton>
            </div>
          </div>
        </div>
      )}
      {webActivityModalOpen && (
        <ViewWebActivityModal
          closeWebActivityModal={closeWebActivityModal}
          uuid={personAttributes?.uuid}
          visits={visits(personAttributes?.idx_visits)}
          lastVisit={lastVisit(personAttributes?.idx_last_visit)}
          views={valueOrZero(personAttributes?.idx_property_views)}
          isFavorite={false}
        />
      )}
    </CollapsibleElement>
  );
};

WebActivity.propTypes = {
  openPlatformCTA: PropTypes.func.isRequired,
};

export default WebActivity;
